import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Slider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components for NEXUS design
const ComparisonContainer = styled(Box)(({ theme }) => ({
  border: '1px solid #00F0FF',
  backgroundColor: 'rgba(11, 8, 19, 0.6)',
  padding: '25px',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '15px',
  },
}));

const ComparisonTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '20px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    marginBottom: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    marginBottom: '10px',
  },
}));

const ControlsBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '10px',
  },
}));

const NexusIconButton = styled(IconButton)(({ theme }) => ({
  border: '1px solid #6B6A78',
  color: '#00F0FF',
  '&:hover': {
    borderColor: '#00F0FF',
    backgroundColor: 'rgba(0, 240, 255, 0.1)',
    boxShadow: '0 0 10px rgba(0, 240, 255, 0.3)',
  },
}));

const ZoomText = styled(Typography)(({ theme }) => ({
  color: '#00F0FF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '12px',
  minWidth: '50px',
  textAlign: 'center',
}));

const NexusToggleButton = styled(ToggleButton)(({ theme }) => ({
  border: '1px solid #6B6A78',
  color: '#6B6A78',
  '&.Mui-selected': {
    borderColor: '#00F0FF',
    color: '#00F0FF',
    backgroundColor: 'rgba(0, 240, 255, 0.1)',
  },
  '&:hover': {
    borderColor: '#00F0FF',
  },
}));

const CanvasContainer = styled(Box)(({ theme }) => ({
  border: '1px solid #39FF14',
  backgroundColor: 'rgba(57, 255, 20, 0.05)',
  borderRadius: '5px',
  padding: '10px',
}));

const NexusCanvas = styled('canvas')(({ theme }) => ({
  width: '100%',
  maxWidth: '600px',
  height: '400px',
  border: '1px solid #39FF14',
  borderRadius: '5px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    height: '300px',
  },
  [theme.breakpoints.down('sm')]: {
    height: '200px',
  },
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  marginTop: '20px',
}));

const SliderLabel = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginBottom: '10px',
}));

const NexusSlider = styled(Slider)(({ theme }) => ({
  color: '#00F0FF',
  '& .MuiSlider-thumb': {
    backgroundColor: '#00F0FF',
    boxShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
  },
  '& .MuiSlider-track': {
    backgroundColor: '#00F0FF',
  },
  '& .MuiSlider-rail': {
    backgroundColor: '#6B6A78',
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '600px',
  border: '1px solid #39FF14',
  backgroundColor: 'rgba(57, 255, 20, 0.05)',
  borderRadius: '5px',
  padding: '10px',
}));

const ComparisonImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '400px',
  objectFit: 'contain',
  border: '1px solid #39FF14',
  borderRadius: '5px',
  [theme.breakpoints.down('md')]: {
    height: '300px',
  },
  [theme.breakpoints.down('sm')]: {
    height: '200px',
  },
}));

const ImageBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '20px',
  left: '20px',
  backgroundColor: 'rgba(11, 8, 19, 0.9)',
  color: '#39FF14',
  padding: '5px 10px',
  borderRadius: '3px',
  border: '1px solid #39FF14',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '10px',
}));

const NexusButton = styled(Button)<{ nexusVariant?: 'primary' | 'secondary' | string }>(({ nexusVariant = 'primary', theme }) => ({
  border: nexusVariant === 'primary' ? '2px solid #00F0FF' : '2px solid #FFD200',
  color: nexusVariant === 'primary' ? '#00F0FF' : '#FFD200',
  padding: '12px 25px',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    padding: '10px 20px',
    fontSize: '10px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 15px',
    fontSize: '9px',
  },
  '&:hover': {
    borderColor: nexusVariant === 'primary' ? '#00F0FF' : '#FFD200',
    color: nexusVariant === 'primary' ? '#00F0FF' : '#FFD200',
    boxShadow: `0 0 15px ${nexusVariant === 'primary' ? 'rgba(0, 240, 255, 0.5)' : 'rgba(255, 210, 0, 0.5)'}`,
    transform: 'translateY(-2px)',
  },
  '&.Mui-selected': {
    backgroundColor: nexusVariant === 'primary' ? '#00F0FF' : '#FFD200',
    color: '#0B0813',
  },
}));

const TipBox = styled(Box)(({ theme }) => ({
  marginTop: '20px',
  padding: '15px',
  border: '1px solid #6B6A78',
  backgroundColor: 'rgba(11, 8, 19, 0.4)',
  borderRadius: '5px',
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
  },
}));

const TipText = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

interface ImageComparisonProps {
  originalImage: string;
  encodedImage: string;
}

const ImageComparison: React.FC<ImageComparisonProps> = ({
  originalImage,
  encodedImage
}) => {
  const [sliderValue, setSliderValue] = useState(50);
  const [zoom, setZoom] = useState(1);
  const [viewMode, setViewMode] = useState<'slider' | 'toggle'>('slider');
  const [showOriginal, setShowOriginal] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (viewMode === 'slider' && canvasRef.current && originalImage && encodedImage) {
      drawSliderComparison();
    }
  }, [sliderValue, zoom, viewMode, originalImage, encodedImage]);

  const drawSliderComparison = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img1 = new Image();
    const img2 = new Image();

    img1.onload = () => {
      img2.onload = () => {
        // Set canvas size
        canvas.width = 600;
        canvas.height = 400;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Calculate dimensions with zoom
        const width = canvas.width * zoom;
        const height = canvas.height * zoom;
        const offsetX = (canvas.width - width) / 2;
        const offsetY = (canvas.height - height) / 2;

        // Draw original image on the left
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, (canvas.width * sliderValue) / 100, canvas.height);
        ctx.clip();
        ctx.drawImage(img1, offsetX, offsetY, width, height);
        ctx.restore();

        // Draw encoded image on the right
        ctx.save();
        ctx.beginPath();
        ctx.rect((canvas.width * sliderValue) / 100, 0, canvas.width, canvas.height);
        ctx.clip();
        ctx.drawImage(img2, offsetX, offsetY, width, height);
        ctx.restore();

        // Draw slider line
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo((canvas.width * sliderValue) / 100, 0);
        ctx.lineTo((canvas.width * sliderValue) / 100, canvas.height);
        ctx.stroke();
      };
      img2.src = encodedImage;
    };
    img1.src = originalImage;
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'slider' | 'toggle' | null,
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  return (
    <ComparisonContainer>
      <ComparisonTitle>WORLD COMPARISON</ComparisonTitle>

      {/* Controls */}
      <ControlsBar>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
        >
          <NexusToggleButton value="slider">
            📊
          </NexusToggleButton>
          <NexusToggleButton value="toggle">
            👁️
          </NexusToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NexusIconButton onClick={handleZoomOut} size="small">
            ➖
          </NexusIconButton>
          <ZoomText>{Math.round(zoom * 100)}%</ZoomText>
          <NexusIconButton onClick={handleZoomIn} size="small">
            ➕
          </NexusIconButton>
        </Box>
      </ControlsBar>

      {/* Image Display */}
      {viewMode === 'slider' ? (
        <Box>
          <CanvasContainer>
            <NexusCanvas
              ref={canvasRef}
            />
          </CanvasContainer>
          <SliderContainer>
            <SliderLabel>// SLIDER POSITION: {sliderValue}%</SliderLabel>
            <NexusSlider
              value={sliderValue}
              onChange={(_, value) => setSliderValue(value as number)}
              min={0}
              max={100}
              step={1}
              marks={[
                { value: 0, label: 'ORIGINAL' },
                { value: 50, label: '50%' },
                { value: 100, label: 'ENCODED' }
              ]}
            />
          </SliderContainer>
        </Box>
      ) : (
        <Box>
          <ImageContainer>
            <ComparisonImage
              src={showOriginal ? originalImage : encodedImage}
              alt={showOriginal ? "Original" : "Encoded"}
            />
            <ImageBadge>
              {showOriginal ? 'ORIGINAL' : 'ENCODED'}
            </ImageBadge>
          </ImageContainer>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <NexusButton
              nexusVariant={showOriginal ? "primary" : "secondary"}
              onClick={() => setShowOriginal(true)}
              fullWidth
            >
              SHOW ORIGINAL
            </NexusButton>
            <NexusButton
              nexusVariant={!showOriginal ? "primary" : "secondary"}
              onClick={() => setShowOriginal(false)}
              fullWidth
            >
              SHOW ENCODED
            </NexusButton>
          </Box>
        </Box>
      )}

      {/* Legend */}
      <TipBox>
        <TipText>
          // TIP: USE THE SLIDER TO COMPARE IMAGES SIDE-BY-SIDE, OR TOGGLE BETWEEN VIEWS TO SPOT DIFFERENCES.
          HIGHER ZOOM LEVELS HELP DETECT SUBTLE CHANGES IN THE IMAGE.
        </TipText>
      </TipBox>
    </ComparisonContainer>
  );
};

export default ImageComparison;
